import { AnyAction } from 'redux';
import { Dispatch } from 'react';

import { Logger } from '@utils/logger';
import { setEditImageModal } from '@redux/format-checker/formatChecker';

export type sectionType = {
  start: RegExp;
  allowedHeading: string[];
  sectionName: string;
};

const logger = new Logger('error-extractor.ts');

export interface customDiagnosticType {
  from: number;
  to: number;
  message?: string;
  severity: 'info' | 'warning' | 'error';
  lineNo?: number;
  renderMessage?: Function;
};

type warningArrType = Omit<customDiagnosticType, 'severity'> & { severity: 'warning' };

/**
 * here approach is
 * we have some reserved heading for each section
 * so we will check if any heading other than allowed heading is present in the section
 * then we will show error
 */

// reserved headings
const RegExpMap: {
  [key: string]: RegExp;
} = {
  '## Overview': /^##\s*Overview\s*$/i,
  '## Introduction': /^##\s*Introduction\s*$/i,
  '## Scope of article': /^##\s*Scope of article\s*$/i,
  '## Scope': /^##\s*Scope\s*$/i,
  '## Conclusion': /^##\s*Conclusion\s*$/i,
  '## Summary': /^##\s*Summary\s*$/i,
  '## FAQs': /^##\s*FAQs\s*$/i,
  '## FAQ': /^##\s*FAQ\s*$/i
};

// reserved headings for each section
const allSectionData: sectionType[] = [
  {
    start: /^\s*:::section\{\.abstract\}$/,
    allowedHeading: ['## Overview', '## Introduction'],
    sectionName: 'abstract'
  },
  {
    start: /^\s*:::section\{\.scope\}$/,
    allowedHeading: ['## Scope of article', '## Scope'],
    sectionName: 'scope'
  },
  {
    start: /^\s*:::section\{\.summary\}$/,
    allowedHeading: ['## Conclusion', '## Summary'],
    sectionName: 'summary'
  },
  {
    start: /^\s*:::section\{\.faq-section\}$/,
    allowedHeading: ['## FAQs', '## FAQ'],
    sectionName: 'faq'
  },
  {
    start: /^\s*:::section\{\.main\}$/,
    allowedHeading: ['## Introduction'],
    sectionName: 'main'
  }
];

// these are the section which have only allowed heading
const sectionWithStrictHeading: string[] = ['summary', 'faq', 'abstract'];

function getNotAllowedHeading<T>(arr: T[], value: T[]): T[] {
  let newArr: T[] = [];
  newArr = arr.filter((e) => !value.includes(e));
  return newArr;
}

const allHeading = [
  '## Overview',
  '## Introduction',
  '## Scope of article',
  '## Scope',
  '## Conclusion',
  '## Summary',
  '## Conclusion',
  '## FAQs',
  '## FAQ'
];

// allowed section
const allSection = [
  'abstract',
  'scope',
  'summary',
  'main',
  'tip',
  'quiz_pop',
  'faq-section'
];


const regexToDetectH1 = /^#\s.+/;
const regexToDetectText = /^.+/;
const regexToDetectSection = /^:::section{[.]([^\s]+)}\s*$/;
const regexSectionEnd = /^:::\s*$/;
const regexToDetectCodeBlockStart = /^```.+$/;
const regexToDetectCodeBlockEnd = /^```$/;
const regexToDetectYAML = /^---$/;
const regexToDetectH2 = /^##\s.+/;
const regexToDetectImageUrl = /https:\/\/[^\/]+\/(topics\/(media|images)\/[^\/]+\.(webp|png|jpg|jpeg))/i;

/**
 * @param lines markdown string array of lines
 * @returns array of Diagnose object
 */

const renderEditNode = (
  dispatch: Dispatch<AnyAction>,
  imageDataTrigger: Function,
  imageFilename: string
): Node => {
  const editButton = document.createElement('button');
  editButton.textContent = 'Edit Image';
  editButton.className = 'btn btn-primary btn-sm';
  editButton.addEventListener('click', () => {
    dispatch(setEditImageModal(true));
    imageDataTrigger(imageFilename);
  });
  return editButton;
};

export const errorExtractor = (
  newLines: string[],
  lineStartIndexes: number[],
  dispatch: Dispatch<AnyAction>,
  imageDataTrigger: Function,
  hasImageToolsAccess: boolean
): {
  issuesArr: customDiagnosticType[];
  toolsArr: customDiagnosticType[];
  warningArr: warningArrType[];
} => {
  const regexSectionBeginArr: RegExp[] = allSectionData.map((e) => e.start);
  let strictMode = false;
  const issuesArr: customDiagnosticType[] = [];
  const warningArr: warningArrType[] = [];
  const tempIssuesArr: customDiagnosticType[] = [];
  const toolsArr: customDiagnosticType[] = [];
  /**
   * switch variable which tell the opening and closing window of section
   * 1 -> section starts
   * 0 -> section ends
   */
  let switchVal = 0;
  /**
   * Need to use this variable because switch val is used for researved section
   * sectionPairSwitch variable which tell the current section pair
   * 0 -> start
   * 1 -> end
   * 2 -> outside section
   */
  let sectionPairSwitch = 0;
  let currentSectionStatus = {
    isCompleted: false,
    completedAt: 0,
    startedAt: 0
  };

  // switch variable which tell the opening and closing window of code block
  let codeBlockSwitch = 0;
  let codeBlockEnd = 0;
  let firstSectionStart = {
    isStarted: false,
    startedAt: 0
  };
  // variable to save current regExp heading and its section type
  let currentSection: {
    sectionName: string;
    sectionHeading: string[];
  } = {
    sectionName: '',
    sectionHeading: []
  };
  // array of headings which are not allowed in current section
  let notAllowedHeadings: string[] = [];
  let beginTextSearchOutsideSection = false;

  // last section start and end line no index
  const lastSectionStartEndIndex = newLines.reduce(
    (prevLine, currLine, index) => {
      if (regexToDetectSection.test(currLine)) {
        prevLine.start = index;
      }

      if (regexSectionEnd.test(currLine)) {
        prevLine.end = index;
      }
      return prevLine;
    },
    { start: -1, end: -1 } as {
      start: number;
      end: number;
    }
  );

  // check if last section is open or not
  const isLastSectionOpen = (index: number) =>
    lastSectionStartEndIndex.start === index &&
    lastSectionStartEndIndex.start > lastSectionStartEndIndex.end;



  for (let i = 0; i < newLines.length; i++) {
    // if image url is present in the line then we will show edit button
    if (hasImageToolsAccess && regexToDetectImageUrl.test(newLines[i])) {
      let imageUrl = newLines[i].match(regexToDetectImageUrl)?.[0];
      if (imageUrl) {
        imageUrl = imageUrl.split('/').pop();
        toolsArr.push({
          from: lineStartIndexes[i],
          to: lineStartIndexes[i] + 1,
          renderMessage: () =>
            renderEditNode(dispatch, imageDataTrigger, imageUrl),
          severity: 'info'
        });
      } else {
        logger.error('Error in extracting image url');
      }
    }
    if (
      regexToDetectCodeBlockStart.test(newLines[i]) &&
      codeBlockSwitch === 0
    ) {
      codeBlockSwitch = 1;
    }
    if (regexToDetectCodeBlockEnd.test(newLines[i]) && codeBlockSwitch === 1) {
      codeBlockSwitch = 0;
      codeBlockEnd = i;
    }
    // if code block is present in the line then we will show error
    if (
      codeBlockEnd < i &&
      regexToDetectCodeBlockEnd.test(newLines[i]) &&
      codeBlockSwitch === 0
    ) {
      issuesArr.push({
        from: lineStartIndexes[i],
        to: lineStartIndexes[i] + 1,
        message: 'Code block with no language name is not allowed.',
        severity: 'error',
        lineNo: i + 1
      });
    }
    // if h1 is present in the line then we will show error
    if (
      codeBlockEnd < i &&
      codeBlockSwitch === 0 &&
      newLines[i].match(regexToDetectH1)
    ) {
      issuesArr.push({
        from: lineStartIndexes[i],
        to: lineStartIndexes[i] + 1,
        message: 'H1 is not allowed in the article.',
        severity: 'error',
        lineNo: i + 1
      });
    }
    for (let j in regexSectionBeginArr) {
      const isSectionStart =
        regexSectionBeginArr[j].test(newLines[i]) && switchVal === 0;
      const isSectionEnd = regexSectionEnd.test(newLines[i]) && switchVal === 1;
      if (isSectionStart) {
        currentSection.sectionName = allSectionData[j].sectionName;
        currentSection.sectionHeading = allSectionData[j].allowedHeading;
        switchVal = 1;
        notAllowedHeadings = getNotAllowedHeading(
          allHeading,
          currentSection.sectionHeading
        );
        break;
      }

      if (isSectionEnd) {
        switchVal = 0;
        notAllowedHeadings = [];
        break;
      }
    }
    if (sectionWithStrictHeading.includes(currentSection.sectionName)) {
      strictMode = true;
    } else {
      strictMode = false;
    }
    if (regexToDetectSection.test(newLines[i]) && sectionPairSwitch === 0) {
      if (!firstSectionStart.isStarted) {
        firstSectionStart.isStarted = true;
        firstSectionStart.startedAt = i;
      }
      const sectionName = regexToDetectSection.exec(newLines[i])?.[1];
      if (sectionName && !allSection.includes(sectionName)) {
        issuesArr.push({
          from: lineStartIndexes[i],
          to: lineStartIndexes[i] + 1,
          message: `Section name ${sectionName ? `"${sectionName}" is not allowed`: `is empty`}.`,
          severity: 'error',
          lineNo: i + 1
        });
      }
      beginTextSearchOutsideSection = true;
      sectionPairSwitch = 1;
      currentSectionStatus.startedAt = i;
      currentSectionStatus.isCompleted = false;
    }
    if (regexSectionEnd.test(newLines[i]) && sectionPairSwitch === 1) {
      sectionPairSwitch = 0;
      currentSectionStatus.isCompleted = true;
      currentSectionStatus.completedAt = i;
    }
    /**
     * if new section started before the current section end
     * and last section is not closed then show error
     */
    if (
      (firstSectionStart.isStarted &&
      firstSectionStart.startedAt < i) &&
      ((regexToDetectSection.test(newLines[i]) || isLastSectionOpen(i - 1)) &&
      currentSectionStatus.startedAt < i) &&
      sectionPairSwitch === 1
    ) {
      issuesArr.push({
        from: lineStartIndexes[currentSectionStatus.startedAt],
        to: lineStartIndexes[currentSectionStatus.startedAt] + 1,
        message: 'Section is not closed.',
        severity: 'error',
        lineNo: currentSectionStatus.startedAt + 1
      });
    }
    // if text is present outside the section then we will show error
    if (
      newLines[i].length > 0 &&
      beginTextSearchOutsideSection &&
      currentSectionStatus.isCompleted &&
      currentSectionStatus.completedAt < i
    ) {
      if (regexToDetectText.test(newLines[i])) {
        issuesArr.push({
          from: lineStartIndexes[i],
          to: lineStartIndexes[i] + 1,
          message: 'Text is not allowed outside the section.',
          severity: 'error',
          lineNo: i + 1
        });
      }
    }
    if (switchVal === 1 && notAllowedHeadings.length > 0) {
      if (strictMode) {
        if (regexToDetectH2.test(newLines[i])) {
          let hasError = true;
          for (let heading of currentSection.sectionHeading) {
            if (RegExpMap[heading].test(newLines[i])) {
              hasError = false;
            }
          }
          // strict heading is not present in the section then we will show error
          if (hasError) {
            issuesArr.push({
              from: lineStartIndexes[i],
              to: lineStartIndexes[i] + 1,
              message: `Only ${currentSection.sectionHeading.join(
                ' or '
              )} is allowed in the ${currentSection.sectionName} section.`,
              severity: 'error',
              lineNo: i + 1
            });
          }
        }
      } else {
        for (let heading of notAllowedHeadings) {
          if (RegExpMap[heading].test(newLines[i])) {
            issuesArr.push({
              from: lineStartIndexes[i],
              to: lineStartIndexes[i] + 1,
              message: `${heading} is not allowed in the ${currentSection.sectionName} section.`,
              severity: 'error',
              lineNo: i + 1
            });
            break;
          }
        }
      }
    }
  }
  let yamlSwitch = 0;
  let yamlStartIndex = 0;
  let yamlEndIndex = 0;
  let yamlIndexSlice = 0;

  if (firstSectionStart.isStarted) {
    for (let i = 0; i < firstSectionStart.startedAt; i++) {
      if (regexToDetectYAML.test(newLines[i]) && yamlSwitch === 0) {
        yamlSwitch = 1;
        yamlStartIndex = i;
      }
      if (
        regexToDetectYAML.test(newLines[i]) &&
        yamlSwitch === 1 &&
        yamlStartIndex < i
      ) {
        yamlEndIndex = i;
        // 2 -> because yaml only allowed at the start of the file
        yamlSwitch = 2;
      }
      // if text is present outside the section then we will show error after yaml and before first heading
      if (newLines[i].length > 0)
        tempIssuesArr.push({
          from: lineStartIndexes[i],
          to: lineStartIndexes[i] + 1,
          message: 'Text not allowed outside the section.',
          severity: 'error',
          lineNo: i + 1
        });
    }
    yamlIndexSlice =
      yamlSwitch === 2 && yamlStartIndex === 0 ? yamlEndIndex + 1 : 0;
    issuesArr.push(
      ...tempIssuesArr.slice(yamlIndexSlice, firstSectionStart.startedAt + 1)
    );
  } else {
    // check for text outside the section if section is not present
    for (let i = 0; i < newLines.length; i++) {
      if (newLines[i].length > 0)
        tempIssuesArr.push({
          from: lineStartIndexes[i],
          to: lineStartIndexes[i] + 1,
          message: 'Text not allowed outside the section.',
          severity: 'error',
          lineNo: i + 1
        });
      if (regexToDetectYAML.test(newLines[i])) {
        if (yamlSwitch === 0) {
          yamlSwitch = 1;
          yamlStartIndex = i;
        }
        if (yamlSwitch === 1 && yamlStartIndex < i) {
          yamlEndIndex = i;
          // 2 -> because yaml only allowed at the start of the file
          yamlSwitch = 2;
        }
      }
    }
    yamlIndexSlice =
      yamlSwitch === 2 && yamlStartIndex === 0 ? yamlEndIndex + 1 : 0;
    issuesArr.push(...tempIssuesArr.slice(yamlIndexSlice));
  }
  // if yaml is not present at the start of the article then we will show warning
  if (!regexToDetectYAML.test(newLines[0]) || yamlSwitch !== 2) {
    warningArr.push({
      from: lineStartIndexes[0],
      to: lineStartIndexes[0] + 1,
      message: 'Yaml is not present at the start of the article.',
      severity: 'warning',
      lineNo: 0
    });
  }
  // joining issues and tools array

  return { issuesArr, toolsArr, warningArr };
};
